import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { CarteraCastigadaService } from 'src/app/services/cartera-castigada/cartera-castigada.service';

/**
 * Módulo Cartera Castigada (Pérdidas Incobrables) — Parámetros de Sistema.
 * Tres secciones: Consulta (clientes/créditos/cuotas), Lotes (simular cortes) y
 * Cuentas contables (gestión).
 */
@Component({
  selector: 'app-cartera-castigada',
  templateUrl: './cartera-castigada.component.html',
})
export class CarteraCastigadaComponent implements OnInit, OnDestroy {
  tab: 'consulta' | 'lotes' | 'cuentas' = 'consulta';

  // Drawer de detalle del cliente (patrón NgbOffcanvas, igual que
  // contact-financial-payments / client-installments-view).
  @ViewChild('detalleDrawer') detalleDrawer!: TemplateRef<any>;

  // ── Consulta ──
  resumen: any = null;
  loadingResumen = false;
  clientes: any[] = [];
  loadingClientes = false;
  total = 0;
  page = 1;
  limit = 20;
  pages = 0;
  q = '';
  estado = ''; // '' | 'SOLO' | 'MIXTA'
  lote = ''; // '' = todos los lotes
  estatusCredito = ''; // '' | 'PENDIENTE' | 'PAGADO'
  lotesDisponibles: any[] = [];
  seleccionado: any = null;
  detalle: any = null;
  loadingDetalle = false;

  // ── Lotes ──
  corte = '2025-12-17';
  simulando = false;
  simulacion: any = null;
  generandoMsg = '';
  // Listado paginado de clientes de la simulación
  simItems: any[] = [];
  simTotal = 0;
  simPage = 1;
  simLimit = 20;
  simPages = 0;
  simQ = '';
  loadingSim = false;
  descargando = false;
  // Marcaje real del lote (POST /simular/crear + polling /lote/estado)
  marcando = false;
  marcandoMsg = '';
  private lotePollTimer: any = null;
  // Progreso en tiempo real del marcaje (contrato /lote/estado enriquecido)
  loteEnCurso: string | number | null = null;
  loteFase: 'creditos' | 'clientes' | 'listo' | null = null;
  lotePorcentaje = 0;
  loteCreditosMarcados = 0;
  loteTotalCreditos = 0;
  loteClientesMarcados = 0;
  loteTotalClientes = 0;
  // Auto-refresco suave de la Consulta mientras el marcaje escribe clientes.
  consultaActualizando = false;
  private ultimoRefrescoConsulta = 0;

  // ── Cuentas contables ──
  cuentas: any[] = [];
  loadingCuentas = false;
  opcionesCuenta: any[] = [];
  usaOpciones = false;

  constructor(
    private svc: CarteraCastigadaService,
    private offcanvasService: NgbOffcanvas,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.cargarResumen();
    this.cargarClientes();
    this.cargarLotes();
    // Si al montar el componente ya hay un marcaje EN CURSO (aunque sea de otro
    // corte, p. ej. tras recargar la página), saltamos a Lotes y mostramos el
    // progreso + la tabla, sin perder la vista. El trabajo corre en el servidor.
    this.resumirMarcajeActivo();
  }

  // Busca cualquier marcaje en curso (sin depender del corte del input) y, si lo
  // hay, restaura la vista: pestaña Lotes + barra de progreso + tabla de clientes.
  private resumirMarcajeActivo = async () => {
    if (this.marcando) return;
    let r: any;
    try { r = await this.svc.loteActivo(); } catch (_e) { return; }
    const act = r?.activo;
    if (!act) return;
    this.corte = String(act.corte).slice(0, 10); // ISO → 'YYYY-MM-DD' (el input de corte)
    this.tab = 'lotes';
    this.marcando = true;
    this.aplicarProgreso(act);
    this.iniciarPollingLote();
    this.simPage = 1;
    this.cargarSimClientes(); // restaura la tabla "Clientes que entrarían" del corte en curso
  };

  ngOnDestroy(): void {
    this.detenerPollingLote();
  }

  cambiarTab = (t: 'consulta' | 'lotes' | 'cuentas') => {
    this.tab = t;
    if (t === 'cuentas' && !this.cuentas.length && !this.loadingCuentas) {
      this.cargarCuentas();
    }
    // Al entrar a Lotes verificamos si hay un marcaje en curso para retomar
    // el progreso en vivo (el trabajo corre en el servidor pase lo que pase).
    if (t === 'lotes') {
      this.resumirMarcajeSiCorresponde();
    }
  };

  // ════════════ CONSULTA ════════════
  cargarResumen = async () => {
    this.loadingResumen = true;
    try {
      const r = await this.svc.resumen(this.lote || undefined);
      this.resumen = r?.resumen || null;
    } catch (e: any) {
      this.resumen = null;
    } finally {
      this.loadingResumen = false;
    }
  };

  cargarClientes = async () => {
    this.loadingClientes = true;
    try {
      const r = await this.svc.clientes({ page: this.page, limit: this.limit, q: this.q, estado: this.estado, lote: this.lote, estatus_credito: this.estatusCredito });
      this.clientes = r?.items || [];
      this.total = r?.total || 0;
      this.pages = r?.pages || 0;
    } catch (e: any) {
      this.clientes = [];
      Swal.fire('Error', e?.error?.error || 'No se pudo cargar el listado.', 'error');
    } finally {
      this.loadingClientes = false;
    }
  };

  buscar = () => { this.page = 1; this.cargarClientes(); };

  filtrarEstado = (v: string) => { this.estado = v; this.page = 1; this.cargarClientes(); };
  filtrarEstatus = (v: string) => { this.estatusCredito = v; this.page = 1; this.cargarClientes(); };

  filtrarLote = (v: string) => { this.lote = v; this.page = 1; this.cargarClientes(); this.cargarResumen(); };

  cargarLotes = async () => {
    try {
      const r = await this.svc.lotes();
      this.lotesDisponibles = r?.items || [];
    } catch (_e) {
      this.lotesDisponibles = [];
    }
  };

  irPagina = (p: number) => {
    if (p < 1 || p > this.pages) return;
    this.page = p;
    this.cargarClientes();
  };

  verCliente = async (c: any) => {
    this.seleccionado = c;
    this.detalle = null;
    this.loadingDetalle = true;
    // Abre el drawer inmediatamente (muestra spinner mientras carga).
    this.offcanvasService.open(this.detalleDrawer, {
      position: 'end',
      panelClass: 'cc-drawer',
      keyboard: true,
    });
    try {
      const r = await this.svc.cliente(c.person_id, this.lote || undefined);
      this.detalle = r;
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo cargar el detalle.', 'error');
    } finally {
      this.loadingDetalle = false;
    }
  };

  cerrarDetalle = () => {
    this.offcanvasService.dismiss('close');
    this.seleccionado = null;
    this.detalle = null;
  };

  // Código (uuid) del cliente para navegar a Compras financiadas.
  private codigoCliente = (): string | null =>
    this.detalle?.cliente?.code || this.seleccionado?.code || null;

  verComprasFinanciadas = () => {
    const code = this.codigoCliente();
    if (!code) {
      Swal.fire('No disponible', 'Este cliente no tiene un código asociado.', 'info');
      return;
    }
    this.offcanvasService.dismiss('navigate');
    this.router.navigate(['/console/person/financial-payments', code]);
  };

  // ════════════ LOTES ════════════
  private corteIso = (): string => new Date(this.corte + 'T04:00:00.000Z').toISOString();

  // Texto legible de la fase actual del marcaje.
  get faseLabel(): string {
    switch (this.loteFase) {
      case 'creditos': return 'Marcando créditos';
      case 'clientes': return 'Marcando clientes';
      case 'listo': return 'Finalizando';
      default: return 'Preparando…';
    }
  }

  // Consulta el estado del corte actual y, si hay un marcaje en curso,
  // retoma la barra de progreso y el polling (idempotente).
  private resumirMarcajeSiCorresponde = async () => {
    if (this.marcando) return; // ya lo estamos mostrando/polling
    let est: any;
    try {
      est = await this.svc.loteEstado(this.corteIso());
    } catch (_e) {
      return;
    }
    if (est?.estado === 'marcando') {
      this.marcando = true;
      this.aplicarProgreso(est);
      this.iniciarPollingLote();
    }
  };

  // Vuelca los campos de progreso del contrato /lote/estado al estado local.
  private aplicarProgreso = (est: any) => {
    this.loteEnCurso = est?.lote ?? this.loteEnCurso;
    this.loteFase = est?.fase ?? null;
    this.lotePorcentaje = Math.max(0, Math.min(100, Number(est?.porcentaje) || 0));
    this.loteCreditosMarcados = Number(est?.creditos_marcados) || 0;
    this.loteTotalCreditos = Number(est?.total_creditos) || 0;
    this.loteClientesMarcados = Number(est?.clientes_marcados) || 0;
    this.loteTotalClientes = Number(est?.total_clientes) || 0;
  };

  // Reinicia el estado de progreso al terminar/abortar el marcaje.
  private resetProgreso = () => {
    this.loteEnCurso = null;
    this.loteFase = null;
    this.lotePorcentaje = 0;
    this.loteCreditosMarcados = 0;
    this.loteTotalCreditos = 0;
    this.loteClientesMarcados = 0;
    this.loteTotalClientes = 0;
    this.consultaActualizando = false;
  };

  simular = async (force = false) => {
    this.simulando = true;
    this.simulacion = null;
    this.generandoMsg = '';
    try {
      const r = await this.svc.simular(this.corteIso(), force);
      if (r?.generando) {
        // Generación en segundo plano: se consulta el estado hasta que quede lista.
        this.generandoMsg = 'Generando la simulación del corte… puede tardar 1–2 min. Podés seguir trabajando.';
        await this.esperarSimulacion();
        return;
      }
      this.simulacion = r;
      this.simPage = 1;
      await this.cargarSimClientes();
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo simular.', 'error');
    } finally {
      this.simulando = false;
    }
  };

  // Consulta /simular/estado hasta 'listo' (o 'error'), con tope de intentos.
  private esperarSimulacion = async () => {
    const corte = this.corteIso();
    const maxIntentos = 120; // ~6 min a 3s
    for (let i = 0; i < maxIntentos; i++) {
      await new Promise((res) => setTimeout(res, 3000));
      let est: any;
      try {
        est = await this.svc.simularEstado(corte);
      } catch (_) {
        continue;
      }
      if (est?.estado === 'listo') {
        this.simulacion = { creditos: est.resumen?.creditos || 0, clientes: est.resumen?.clientes || 0, monto_por_cobrar: est.resumen?.monto_por_cobrar || 0 };
        this.generandoMsg = '';
        this.simPage = 1;
        await this.cargarSimClientes();
        return;
      }
      if (est?.estado === 'error') {
        this.generandoMsg = '';
        Swal.fire('Error', est?.error || 'La generación de la simulación falló.', 'error');
        return;
      }
    }
    this.generandoMsg = '';
    Swal.fire('Aviso', 'La simulación está tardando más de lo esperado. Volvé a intentar en unos minutos; el proceso sigue en el servidor.', 'info');
  };

  cargarSimClientes = async () => {
    this.loadingSim = true;
    try {
      const r = await this.svc.simularClientes({
        corte: this.corteIso(),
        page: this.simPage,
        limit: this.simLimit,
        q: this.simQ,
      });
      this.simItems = r?.items || [];
      this.simTotal = r?.total || 0;
      this.simPages = r?.pages || 0;
    } catch (e: any) {
      this.simItems = [];
      Swal.fire('Error', e?.error?.error || 'No se pudo cargar el detalle de la simulación.', 'error');
    } finally {
      this.loadingSim = false;
    }
  };

  buscarSim = () => { this.simPage = 1; this.cargarSimClientes(); };

  irPaginaSim = (p: number) => {
    if (p < 1 || p > this.simPages) return;
    this.simPage = p;
    this.cargarSimClientes();
  };

  // Abre el mismo drawer de detalle a partir de una fila simulada.
  verClienteSim = (row: any) => {
    this.verCliente({ person_id: row.person_id, code: row.code, cedula: row.cedula, nombre: row.nombre });
  };

  descargarExcel = async () => {
    this.descargando = true;
    try {
      const blob = await this.svc.simularExport(this.corteIso());
      const url = window.URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cartera-castigada-simulacion-${this.corte}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo descargar el archivo.', 'error');
    } finally {
      this.descargando = false;
    }
  };

  crearLote = async () => {
    if (this.marcando) return;
    // Debe existir una simulación cargada para el corte actual.
    if (!this.simulacion) {
      Swal.fire(
        'Primero simulá',
        'Simulá el corte para conocer cuántos clientes y créditos entrarían antes de crear el lote.',
        'info'
      );
      return;
    }
    const creditos = this.simulacion.creditos || 0;
    const clientes = this.simulacion.clientes || 0;
    const anio = this.corte.slice(0, 4);
    const res = await Swal.fire({
      title: 'Crear lote',
      html:
        `Vas a marcar como cartera castigada <b>${clientes.toLocaleString()}</b> clientes y ` +
        `<b>${creditos.toLocaleString()}</b> créditos con corte <b>${this.corte}</b>.<br><br>` +
        `<input id="swal-lote" class="swal2-input" style="margin:0" maxlength="32" placeholder="Nombre del lote (auto: ${anio})">` +
        `<div style="font-size:12px;color:#6b7280;margin-top:6px">Dejalo vacío para usar "${anio}". Solo letras, números, guion.</div>` +
        `<br>Es el marcaje <b>REAL</b> sobre la base y queda registrado como un lote. ¿Continuar?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, crear lote',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      preConfirm: () => {
        const v = ((document.getElementById('swal-lote') as HTMLInputElement)?.value || '').trim();
        if (v && !/^[A-Za-z0-9_-]{1,32}$/.test(v)) {
          Swal.showValidationMessage('Nombre de lote inválido (solo letras, números, guion y guion bajo; máx 32).');
          return false;
        }
        return v;
      },
    });
    if (!res.isConfirmed) return;
    const loteNom = (res.value || '').trim();

    this.marcando = true;
    this.marcandoMsg = 'Marcando lote…';
    this.resetProgreso();
    try {
      const r = await this.svc.crearLote({ corte: this.corteIso(), lote: loteNom || undefined });
      this.loteEnCurso = r?.lote ?? null;
      if (r?.yaEnCurso) {
        this.marcandoMsg = 'Ya hay un marcaje en curso para este corte…';
      }
      // Arranca el polling del estado del marcaje.
      this.iniciarPollingLote();
    } catch (e: any) {
      this.marcando = false;
      this.marcandoMsg = '';
      this.resetProgreso();
      Swal.fire('Error', e?.error?.error || 'No se pudo iniciar el marcaje del lote.', 'error');
    }
  };

  // Polling de /lote/estado cada 3s mientras el marcaje esté en curso.
  // Se crea el timer una sola vez; si ya existe, solo dispara una consulta.
  private iniciarPollingLote = () => {
    if (this.lotePollTimer) {
      this.consultarEstadoLote();
      return;
    }
    this.lotePollTimer = setInterval(this.consultarEstadoLote, 3000);
    // Primera consulta inmediata para no esperar 3s.
    this.consultarEstadoLote();
  };

  private detenerPollingLote = () => {
    if (this.lotePollTimer) {
      clearInterval(this.lotePollTimer);
      this.lotePollTimer = null;
    }
  };

  private consultarEstadoLote = async () => {
    let est: any;
    try {
      est = await this.svc.loteEstado(this.corteIso());
    } catch (_e) {
      return; // reintenta en el próximo tick
    }
    if (est?.estado === 'marcando') {
      this.marcando = true;
      this.marcandoMsg = 'Marcando lote…';
      this.aplicarProgreso(est);
      // Los clientes marcados se escriben en la vista de forma incremental:
      // refrescamos la Consulta suavemente para que "aparezcan poco a poco".
      this.refrescarConsultaDuranteMarcaje();
      return;
    }
    if (est?.estado === 'listo') {
      this.detenerPollingLote();
      this.marcando = false;
      this.marcandoMsg = '';
      this.lotePorcentaje = 100;
      this.loteFase = 'listo';
      const loteId = est?.lote ?? this.loteEnCurso ?? '';
      Swal.fire('Lote creado', `Lote ${loteId} creado.`.trim(), 'success');
      // Refresca lotes disponibles, la Consulta y el detalle simulado.
      this.cargarLotes();
      this.cargarResumen();
      this.cargarClientes();
      this.simPage = 1;
      this.cargarSimClientes();
      this.resetProgreso();
      return;
    }
    if (est?.estado === 'error') {
      this.detenerPollingLote();
      this.marcando = false;
      this.marcandoMsg = '';
      this.resetProgreso();
      Swal.fire('Error', est?.error || 'El marcaje del lote falló.', 'error');
      return;
    }
    // estado === 'ninguno' u otro: nada en curso, corta el polling.
    if (est?.estado === 'ninguno') {
      this.detenerPollingLote();
      this.marcando = false;
      this.marcandoMsg = '';
      this.resetProgreso();
    }
  };

  // Refresco suave de la Consulta mientras corre el marcaje: re-consulta la
  // página actual (sin resetear la navegación del usuario ni mostrar spinner),
  // como máximo cada ~5s, para que los clientes recién marcados vayan
  // apareciendo. Solo actúa si el usuario está en la pestaña Consulta.
  private refrescarConsultaDuranteMarcaje = () => {
    if (this.tab !== 'consulta') return;
    const ahora = Date.now();
    if (ahora - this.ultimoRefrescoConsulta < 5000) return;
    this.ultimoRefrescoConsulta = ahora;
    this.refrescarConsultaSilencioso();
  };

  // Igual que cargarClientes pero sin togglear loadingClientes (no reemplaza
  // la tabla por un spinner): solo muestra un discreto "actualizando…".
  private refrescarConsultaSilencioso = async () => {
    this.consultaActualizando = true;
    try {
      const r = await this.svc.clientes({ page: this.page, limit: this.limit, q: this.q, estado: this.estado, lote: this.lote, estatus_credito: this.estatusCredito });
      this.clientes = r?.items || this.clientes;
      this.total = r?.total ?? this.total;
      this.pages = r?.pages ?? this.pages;
    } catch (_e) {
      // silencioso: reintenta en el próximo tick
    } finally {
      this.consultaActualizando = false;
    }
  };

  // ════════════ CUENTAS CONTABLES ════════════
  cargarCuentas = async () => {
    this.loadingCuentas = true;
    try {
      const r = await this.svc.cuentasList();
      this.cuentas = r?.items || r?.cuentas || (Array.isArray(r) ? r : []);
    } catch (e: any) {
      this.cuentas = [];
      Swal.fire('Error', e?.error?.error || 'No se pudieron cargar las cuentas.', 'error');
    } finally {
      this.loadingCuentas = false;
    }
    // Intenta cargar opciones (banco/método) si el endpoint existe.
    try {
      const o = await this.svc.cuentasOpciones();
      const items = o?.items || o?.opciones || (Array.isArray(o) ? o : []);
      if (items && items.length) {
        this.opcionesCuenta = items;
        this.usaOpciones = true;
      }
    } catch (_e) {
      this.usaOpciones = false;
    }
  };

  crearCuenta = async () => {
    const { value: form } = await Swal.fire({
      title: 'Crear cuenta contable',
      html:
        `<input id="cc-banco" class="swal2-input" placeholder="Banco / método">` +
        `<input id="cc-simbolo" class="swal2-input" placeholder="Símbolo (ej. PI-BV01)">` +
        `<input id="cc-numero" class="swal2-input" placeholder="Número de cuenta">` +
        `<input id="cc-desde" type="date" class="swal2-input">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Crear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      preConfirm: () => {
        const banco = (document.getElementById('cc-banco') as HTMLInputElement)?.value?.trim();
        const simbolo = (document.getElementById('cc-simbolo') as HTMLInputElement)?.value?.trim();
        const numero = (document.getElementById('cc-numero') as HTMLInputElement)?.value?.trim();
        const desde = (document.getElementById('cc-desde') as HTMLInputElement)?.value;
        if (!banco || !simbolo || !numero) {
          Swal.showValidationMessage('Banco, símbolo y número son obligatorios.');
          return false;
        }
        return { banco, simbolo, numero, desde };
      },
    });
    if (!form) return;
    try {
      await this.svc.cuentaCrear(form);
      Swal.fire({ icon: 'success', title: 'Cuenta creada', timer: 1800, showConfirmButton: false });
      this.cargarCuentas();
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo crear la cuenta.', 'error');
    }
  };

  asociarCuenta = async (c: any) => {
    let value: any;
    if (this.usaOpciones) {
      const options = this.opcionesCuenta.reduce((acc: any, o: any) => {
        const id = o.id || o._id || o.value || o.codigo;
        const label = o.label || o.nombre || o.banco || o.metodo || String(id);
        acc[id] = label;
        return acc;
      }, {});
      const r = await Swal.fire({
        title: 'Asociar cuenta',
        input: 'select',
        inputOptions: options,
        inputPlaceholder: 'Seleccione banco / método',
        showCancelButton: true,
        confirmButtonText: 'Asociar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2563eb',
        inputValidator: (v) => (!v ? 'Seleccione una opción.' : null),
      });
      if (!r.isConfirmed) return;
      value = { opcion: r.value };
    } else {
      const r = await Swal.fire({
        title: 'Asociar cuenta',
        input: 'text',
        inputPlaceholder: 'Banco / método a asociar',
        showCancelButton: true,
        confirmButtonText: 'Asociar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2563eb',
        inputValidator: (v) => (!v ? 'Ingrese un valor.' : null),
      });
      if (!r.isConfirmed) return;
      value = { metodo: r.value };
    }
    try {
      await this.svc.cuentaAsociar(c.id || c._id, value);
      Swal.fire({ icon: 'success', title: 'Cuenta asociada', timer: 1800, showConfirmButton: false });
      this.cargarCuentas();
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo asociar la cuenta.', 'error');
    }
  };

  toggleCuenta = async (c: any) => {
    const nuevoEstado = !c.activa;
    const res = await Swal.fire({
      title: nuevoEstado ? 'Activar cuenta' : 'Inactivar cuenta',
      text: `¿Desea ${nuevoEstado ? 'activar' : 'inactivar'} la cuenta ${c.simbolo || c.numero || ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
    });
    if (!res.isConfirmed) return;
    try {
      await this.svc.cuentaActualizar(c.id || c._id, { activa: nuevoEstado });
      c.activa = nuevoEstado;
      Swal.fire({ icon: 'success', title: 'Estado actualizado', timer: 1500, showConfirmButton: false });
    } catch (e: any) {
      Swal.fire('Error', e?.error?.error || 'No se pudo actualizar la cuenta.', 'error');
    }
  };
}
